import { inject, provide, type InjectionKey } from 'vue';
import { fetchWithAuth, getApiOrigin } from '../auth';

/**
 * Master's read routes of the search dictionary (I-3024): what stays a request
 * once everything the dictionary stores is a setting of the account config
 * path. Field names are the JSON the backend speaks — do not rename them.
 *
 * `GET /search/dictionary/sources` (the shipped base vocabulary and its
 * licences), `/licences/:name` (a licence text), `/thesaurus?q=` (proposals
 * for a synonym group) and `/explain?q=` (what the search makes of a word,
 * level by level). Requests go to master with no `BLP-Module` header. The
 * entries, the synonym groups, the check, the import and export, the change
 * log and the filters are gone: the settings page reads and writes
 * `spell_check` and `search` instead.
 */

/** The levels the check names: the account, company and tenant, the modules' own words and the base vocabulary. */
export type DictionaryLevel = 'account' | 'company' | 'tenant';

export interface ExplainLevel {
  level: DictionaryLevel | 'module' | 'base';
  contributes: string[];
  wins: boolean;
}

/** One shipped base-vocabulary source (`GET /search/dictionary/sources`). */
export interface DictionarySource {
  name: string;
  author: string;
  url: string;
  version: string;
  licence: string;
  use: string;
  /** Embedded notice text, when this licence has one. */
  notice?: string;
  /** Path of that notice (`/v1/search/dictionary/licences/:name`). */
  noticeUrl?: string;
}

export interface DictionarySources {
  attribution: { de: string; en: string };
  sources: DictionarySource[];
}

/** OpenThesaurus groups for a typed synonym term. */
export interface DictionaryThesaurus {
  groups: string[][];
}

export interface SearchDictionaryClient {
  sources(signal?: AbortSignal): Promise<DictionarySources>;
  /** Licence notice as text. `noticeUrl` is the path from `sources`. */
  licenceNotice(noticeUrl: string, signal?: AbortSignal): Promise<string>;
  thesaurus(q: string, signal?: AbortSignal): Promise<DictionaryThesaurus>;
  explain(term: string, signal?: AbortSignal): Promise<ExplainLevel[]>;
}

/** How a master request is sent. `path` starts at `/search/dictionary`. */
export interface SearchDictionaryApi {
  request(path: string, init?: RequestInit): Promise<Response>;
  /** Absolute URL of a path under `/v1`. */
  url(path: string): string;
}

const ROOT = '/search/dictionary';

/** Master, with the session bearer and no `BLP-Module` header. */
export function createMasterSearchDictionaryApi(): SearchDictionaryApi {
  return {
    url(path: string): string {
      return `${getApiOrigin()}/v1${path}`;
    },
    request(path: string, init?: RequestInit): Promise<Response> {
      const headers = new Headers(init?.headers);
      headers.delete('BLP-Module');
      return fetchWithAuth(this.url(path), { ...init, headers });
    },
  };
}

function withQuery(path: string, params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') continue;
    search.set(key, String(value));
  }
  const query = search.toString();
  return query ? `${path}?${query}` : path;
}

async function readBody<T>(response: Response): Promise<T> {
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  const body = text ? (JSON.parse(text) as unknown) : undefined;
  if (!response.ok) {
    const message =
      body &&
      typeof body === 'object' &&
      'message' in body &&
      typeof (body as { message: unknown }).message === 'string'
        ? (body as { message: string }).message
        : `request failed (${response.status})`;
    // Axios-shaped so `readProblem` can show the detail.
    throw Object.assign(new Error(message), {
      response: { status: response.status, data: body, headers: response.headers },
    });
  }
  return body as T;
}

/** `noticeUrl` from sources, as a path `api.request` can send. */
function licenceRequestPath(noticeUrl: string): string {
  const trimmed = noticeUrl.trim();
  const path = trimmed.startsWith('/v1/') ? trimmed.slice(3) : trimmed;
  const prefix = `${ROOT}/licences/`;
  const name = path.startsWith(prefix) ? path.slice(prefix.length) : '';
  if (!name || name.includes('/') || name.includes('..')) {
    throw new Error('unknown licence notice');
  }
  return prefix + encodeURIComponent(decodeURIComponent(name));
}

/** HTTP client of the read routes. `api` is the master transport. */
export function createHttpSearchDictionaryClient(api: SearchDictionaryApi): SearchDictionaryClient {
  const send = <T>(path: string, init?: RequestInit) =>
    api.request(path, init).then((response) => readBody<T>(response));

  return {
    sources(signal) {
      return send<DictionarySources>(`${ROOT}/sources`, { signal });
    },
    async licenceNotice(noticeUrl, signal) {
      const response = await api.request(licenceRequestPath(noticeUrl), {
        signal,
        headers: { Accept: 'text/plain' },
      });
      const text = await response.text();
      if (!response.ok) throw new Error(text || `request failed (${response.status})`);
      return text;
    },
    thesaurus(q, signal) {
      return send<DictionaryThesaurus>(withQuery(`${ROOT}/thesaurus`, { q }), { signal });
    },
    async explain(term, signal) {
      const body = await send<ExplainLevel[] | { levels: ExplainLevel[] }>(
        withQuery(`${ROOT}/explain`, { q: term }),
        { signal },
      );
      return Array.isArray(body) ? body : body.levels;
    },
  };
}

export const searchDictionaryClientKey: InjectionKey<SearchDictionaryClient> = Symbol(
  'searchDictionaryClient',
);

/** Share one read client. Without a provider the HTTP client is used. */
export function provideSearchDictionaryClient(client: SearchDictionaryClient): void {
  provide(searchDictionaryClientKey, client);
}

export function useSearchDictionaryClient(): SearchDictionaryClient {
  return inject(
    searchDictionaryClientKey,
    () => createHttpSearchDictionaryClient(createMasterSearchDictionaryApi()),
    true,
  );
}

/** Lower-case fold of the stand-in's thesaurus lookup. Not the search fold. */
function fold(value: string): string {
  return value.trim().toLowerCase();
}

export interface MemoryDictionarySeed {
  sources?: DictionarySources;
  /** Licence notice text keyed by `noticeUrl`. */
  licences?: Record<string, string>;
  /** Synonym groups keyed by the lower-case query. */
  thesaurus?: Record<string, string[][]>;
  explain?: ExplainLevel[];
}

/** In-memory stand-in of the read routes for the tests and a visual check before master answers. */
export function createMemorySearchDictionaryClient(
  seed: MemoryDictionarySeed = {},
): SearchDictionaryClient {
  return {
    async sources() {
      const seeded = seed.sources;
      return {
        attribution: {
          de: seeded?.attribution.de ?? '',
          en: seeded?.attribution.en ?? '',
        },
        sources: (seeded?.sources ?? []).map((item) => ({ ...item })),
      };
    },
    async licenceNotice(noticeUrl) {
      const text = seed.licences?.[noticeUrl];
      if (text === undefined) throw new Error('unknown licence notice');
      return text;
    },
    async thesaurus(q) {
      const groups = seed.thesaurus?.[fold(q)] ?? [];
      return { groups: groups.map((group) => [...group]) };
    },
    async explain() {
      return (seed.explain ?? []).map((level) => ({ ...level, contributes: [...level.contributes] }));
    },
  };
}
